import React, { useState, useEffect, useCallback } from "react";
import { Loader2, Check, X, Clock, UserCheck } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";
import { useToast } from "@/components/ui/use-toast";

export default function DeletionRequestsSection() {
  const { effectiveRole } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const list = await base44.entities.AccountDeletionRequest.list("-requested_at", 50);
      const now = new Date();
      const expired = (list || []).filter(
        (r) => r.status === "pending" && new Date(r.expires_at) < now
      );
      if (expired.length > 0) {
        await base44.entities.AccountDeletionRequest.bulkUpdate(
          expired.map((r) => ({ id: r.id, status: "expired" }))
        );
        const updated = await base44.entities.AccountDeletionRequest.list("-requested_at", 50);
        setRequests(updated || []);
      } else {
        setRequests(list || []);
      }
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (effectiveRole !== "admin") return;
    load();
  }, [effectiveRole, load]);

  if (effectiveRole !== "admin") return null;

  const pending = requests.filter((r) => r.status === "pending");

  const handleApprove = async (r) => {
    setActioning(r.id);
    try {
      await base44.entities.User.delete(r.user_id);
      await base44.entities.AccountDeletionRequest.update(r.id, { status: "approved" });
      toast({ title: "Account deleted", description: `${r.user_name || r.user_email} has been removed.` });
      load();
    } catch (e) {
      toast({
        title: "Failed to delete",
        description: e.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setActioning(null);
    }
  };

  const handleDeny = async (r) => {
    setActioning(r.id);
    try {
      await base44.entities.AccountDeletionRequest.update(r.id, { status: "denied" });
      toast({ title: "Request denied" });
      load();
    } catch (e) {
      toast({
        title: "Failed to update",
        description: e.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setActioning(null);
    }
  };

  if (loading || pending.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-amber-200 p-6 space-y-4 dark:bg-slate-900 dark:border-amber-900">
      <div>
        <h3 className="font-semibold text-slate-900 flex items-center gap-2 dark:text-slate-100">
          <UserCheck className="w-4 h-4 text-amber-600" /> Account Deletion Requests
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {pending.length} pending request{pending.length !== 1 ? "s" : ""} awaiting your approval.
        </p>
      </div>
      <div className="space-y-3">
        {pending.map((r) => {
          const expired = new Date(r.expires_at) < new Date();
          return (
            <div key={r.id} className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate dark:text-slate-100">
                    {r.user_name || r.user_email}
                  </p>
                  <p className="text-xs text-slate-500 truncate dark:text-slate-400">{r.user_email}</p>
                </div>
                {expired ? (
                  <span className="text-xs text-slate-400 shrink-0">Expired</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-600 shrink-0">
                    <Clock className="w-3 h-3" />
                    {new Date(r.expires_at).toLocaleString()}
                  </span>
                )}
              </div>
              {!expired && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    className="rounded-xl gap-1.5"
                    disabled={actioning === r.id}
                    onClick={() => handleApprove(r)}
                  >
                    {actioning === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    Approve & Delete
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-xl gap-1.5"
                    disabled={actioning === r.id}
                    onClick={() => handleDeny(r)}
                  >
                    <X className="w-3.5 h-3.5" />
                    Deny
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}